import { toOptionEntry } from '@/entities/rma';
import type { RmaOption } from '@/entities/rma';
import { infoIconTransparent } from '@/shared/ui/assets';
import { DESCRIPTION_ENUM, PATIENT_IMPACT } from '@/shared/config';
import dayjs from 'dayjs';
import _ from 'lodash';
import { t } from 'ttag';

/**
 * Builds the blank RMA device-issue record used when the add-device modal opens or resets after a keep-adding save. Leaves device, replacement device and reasons empty, defaults patient impact to None labelled from the patient impact options, sets the event date to now via dayjs and unsets the complete and maintain-device flags.
 *
 * @param patientImpacts - Patient impact options the None label is read from.
 * @returns A fresh device-issue object carrying the default field values.
 */
const getInitialDeviceIssue = (patientImpacts: RmaOption[]): any => ({
  deviceId: null,
  replacementDeviceId: null,
  reasons: null,
  patientImpact: toOptionEntry(patientImpacts, { value: PATIENT_IMPACT.NONE }),
  inputValue: '',
  dateOfEvent: dayjs(),
  isComplete: false,
  isMaintainDevice: false,
});

/**
 * Removes devices already used by other RMA device issues from a fetched option list. Returns the fetched data untouched when no ids are in use, otherwise keeps only the options whose value matches none of the current device ids.
 *
 * @param fetchedData - Device options returned from the API.
 * @param listCurrentDeviceIds - Device ids already selected on the RMA ticket.
 * @returns The option list with already-used devices removed.
 */
const filterDeviceIssue = (fetchedData: any = [], listCurrentDeviceIds: any = []) => {
  if (_.isEmpty(listCurrentDeviceIds)) {
    return fetchedData;
  }
  /**
   * The fetched device options left after removing every entry whose value already appears among the current RMA device ids. Built by filtering the fetched data and rejecting an entry when some current id equals its value.
   *
   * @returns The filtered list of still-selectable device options.
   */
  const result = _.filter(
    fetchedData,
    (data) => !_.some(listCurrentDeviceIds, (id) => id === data.value),
  );
  return result;
};

/**
 * Picks the reason options of the selected reasons that ask for a free-text describe. Keeps, in option order, every option flagged needsDescribe whose value is among the selected reasons.
 *
 * @param reasons - Reason entries selected on the device issue.
 * @param reasonOptions - Reason options of the device model.
 * @returns The options of the selected reasons that need a describe.
 */
const getOptionsNeedingDescribe = (reasons: any, reasonOptions: RmaOption[]) =>
  _.filter(
    reasonOptions,
    (option) => option.needsDescribe && _.some(reasons, (reason) => reason?.value === option.value),
  );

/**
 * Extends the reason options of a device model with the stored reasons it no longer offers that carry a describe, flagging each as needing one. The API stores a describe only on a needsDescribe option, so a reason deactivated since keeps its describe field on the edit form.
 *
 * @param reasonOptions - Reason options of the device model.
 * @param storedReasons - Reason entries the device issue was opened with.
 * @returns The model's options followed by the described reasons they lack.
 */
const withStoredDescribedReasons = (
  reasonOptions: RmaOption[],
  storedReasons: any,
): RmaOption[] => [
  ...reasonOptions,
  ..._.map(
    _.differenceBy(_.filter(storedReasons, 'describe'), reasonOptions, 'value'),
    ({ value, label }) => ({ value, label, group: null, needsDescribe: true }),
  ),
];

/**
 * Picks the reason options whose describe is required but still empty on the device issue. Narrows the options needing a describe to those whose selected reason carries no describe text.
 *
 * @param reasons - Reason entries selected on the device issue.
 * @param reasonOptions - Reason options of the device model.
 * @returns The options of the selected reasons missing their describe.
 */
const getOptionsMissingDescribe = (reasons: any, reasonOptions: RmaOption[]) =>
  _.filter(
    getOptionsNeedingDescribe(reasons, reasonOptions),
    (option) => !_.find(reasons, (reason) => reason?.value === option.value)?.describe,
  );

/**
 * Resolves the translated label and required-error text of the describe field of a reason. Device errors, damage and other reasons keep their dedicated wording, while any other reason is labelled with its option label and a generic required message.
 *
 * @param option - Reason option whose describe field is shown.
 * @returns The field label and the message shown when the describe is empty.
 */
const getDescribeFieldCopy = ({ value, label }: Pick<RmaOption, 'value' | 'label'>) => {
  switch (value) {
    case DESCRIPTION_ENUM.DEVICE_ERRORS:
      return {
        label: t`Describe the device errors`,
        error: t`Describe the device errors required.`,
      };
    case DESCRIPTION_ENUM.DAMAGE:
      return { label: t`Describe the damage`, error: t`Describe the damage is required.` };
    case DESCRIPTION_ENUM.OTHER:
      return {
        label: t`Describe other reasons`,
        error: t`Describe other reasons
      is required.`,
      };
    default:
      return { label, error: t`Description is required.` };
  }
};

/**
 * Validates one RMA device issue before it joins the ticket, collecting a translated message per offending field. Requires device id, at least one RMA reason, patient impact and date of event, and a describe for every selected reason whose option needs one, keyed by reason value under describes.
 *
 * @param deviceIssue - Device issue record being validated.
 * @param reasonOptions - Reason options of the device model.
 * @returns A map of field name to error message, empty when the issue is valid.
 */
const handleValidate = (deviceIssue: any, reasonOptions: RmaOption[]) => {
  const errors = {};
  const { deviceId, reasons, dateOfEvent, patientImpact } = deviceIssue;
  if (_.isEmpty(deviceId)) {
    _.assign(errors, {
      deviceId: t`Device ID is required.`,
    });
  }
  if (_.isEmpty(reasons)) {
    _.assign(errors, {
      reasons: t`Reason for RMA is required.`,
    });
  }

  const optionsMissingDescribe = getOptionsMissingDescribe(reasons, reasonOptions);
  if (!_.isEmpty(optionsMissingDescribe)) {
    _.assign(errors, {
      describes: _.fromPairs(
        _.map(optionsMissingDescribe, (option) => [
          option.value,
          getDescribeFieldCopy(option).error,
        ]),
      ),
    });
  }

  if (_.isEmpty(patientImpact)) {
    _.assign(errors, {
      patientImpact: t`Patient impact is required.`,
    });
  }
  if (_.isEmpty(dateOfEvent)) {
    _.assign(errors, {
      dateOfEvent: t`Date of event is required.`,
    });
  }

  return errors;
};

/**
 * Renders the informational banner shown when an RMA device is flagged for maintenance, pairing a transparent info icon with the supplied message. Renders nothing when the device is not a maintenance device.
 *
 * @param isMaintenanceDevice - Whether the selected device needs maintenance.
 * @param message - Text shown next to the info icon.
 * @returns The message banner element, or null for a non-maintenance device.
 */
const maintenanceDeviceMessageRenderer = (isMaintenanceDevice: any, message: any) => {
  if (!isMaintenanceDevice) {
    return null;
  }

  return (
    <div className="maintenance-default-value-message-container">
      <img src={infoIconTransparent} alt="info icon" aria-hidden="true" />
      <span className="message">{message}</span>
    </div>
  );
};

export {
  filterDeviceIssue,
  getDescribeFieldCopy,
  getInitialDeviceIssue,
  getOptionsMissingDescribe,
  getOptionsNeedingDescribe,
  handleValidate,
  maintenanceDeviceMessageRenderer,
  withStoredDescribedReasons,
};
